// Pediatric Anesthesia Medications Database
// Organized by class: induction agents, opioids, muscle relaxants, reversal agents, emergency drugs

export const pediatricMedications = [
  // INDUCTION AGENTS
  {
    name: 'Propofol',
    proprietaryName: 'Diprivan',
    class: 'induction',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: '30-60 seconds',
    duration: '5-10 minutes',
    dosing: {
      induction: '2.5-3.5 mg/kg IV (higher in young children)',
      infants: '3-3.5 mg/kg IV',
      neonates: '2-2.5 mg/kg IV (use caution)',
      infusion: '100-300 mcg/kg/min',
      sedation: '25-100 mcg/kg/min',
      units: 'mg/kg IV, mcg/kg/min infusion'
    },
    hemodynamics: {
      HR: '↓',
      BP: '↓↓',
      SVR: '↓↓',
      contractility: '↓'
    },
    uses: [
      'Induction of anesthesia',
      'Maintenance of anesthesia (TIVA)',
      'Sedation for procedures'
    ],
    considerations: [
      'Higher doses needed in children vs adults (higher Vd)',
      'Faster clearance in children',
      'Pain on injection - use large IV or lidocaine pretreatment',
      'AVOID in children <3 years for prolonged sedation (PRIS risk)',
      'Propofol infusion syndrome rare in OR setting',
      'May cause bradycardia, especially with other vagal stimuli',
      'Decreases ICP, cerebral metabolic rate',
      'Antiemetic properties',
      'Contains egg/soy - caution in allergies'
    ],
    bestFor: 'Smooth induction and maintenance in children'
  },
  {
    name: 'Ketamine',
    proprietaryName: 'Ketalar',
    class: 'induction',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: 'IV: 30-60 sec, IM: 3-5 min',
    duration: 'IV: 10-15 min, IM: 15-30 min',
    dosing: {
      induction_iv: '1-2 mg/kg IV',
      induction_im: '4-8 mg/kg IM',
      sedation_iv: '0.25-0.5 mg/kg IV',
      infusion: '5-20 mcg/kg/min',
      po: '6-10 mg/kg PO (bitter taste, variable absorption)',
      units: 'mg/kg'
    },
    hemodynamics: {
      HR: '↑',
      BP: '↑',
      SVR: '↑',
      contractility: '↑'
    },
    uses: [
      'Induction (especially with difficult IV access)',
      'IM induction for uncooperative children',
      'Procedural sedation',
      'Analgesia (sub-dissociative doses)',
      'Bronchospasm (bronchodilator)'
    ],
    considerations: [
      'Preserves airway reflexes and respiratory drive',
      'Excellent for IM induction in uncooperative children',
      'Causes hypersalivation - consider antisialogogue (glycopyrrolate)',
      'May increase ICP, IOP - avoid in head injury',
      'Emergence delirium common (reduce with midazolam)',
      'Nystagmus normal - does not indicate light anesthesia',
      'Safe in asthma (bronchodilator)',
      'Good for burn dressing changes',
      'Cardiovascular stimulation (indirect sympathomimetic)'
    ],
    bestFor: 'IM induction, difficult IV access, asthmatic children'
  },
  {
    name: 'Sevoflurane',
    proprietaryName: 'Ultane',
    class: 'volatile',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: 'Fast (most soluble)',
    duration: 'Rapid emergence',
    dosing: {
      induction: '6-8% (mask induction)',
      maintenance: '1.5-3% (age-dependent MAC)',
      mac_awake: '0.6-1%',
      units: '%'
    },
    mac: {
      neonate: '3.3%',
      infant_6mo: '3.0%',
      child_3yr: '2.8%',
      adult: '2.0%'
    },
    uses: [
      'Inhalational induction (gold standard)',
      'Maintenance of anesthesia',
      'Mask induction in children'
    ],
    considerations: [
      'GOLD STANDARD for mask induction in children',
      'Sweet-smelling, non-pungent',
      'Minimal airway irritation',
      'Faster induction/emergence than isoflurane/desflurane',
      'Higher MAC in infants and children vs adults',
      'May cause emergence delirium (20-30% incidence)',
      'Compound A production with soda lime (not clinically significant)',
      'Dose-dependent myocardial depression',
      'May trigger malignant hyperthermia'
    ],
    bestFor: 'Mask induction in children, maintenance'
  },

  // OPIOIDS
  {
    name: 'Fentanyl',
    proprietaryName: 'Sublimaze',
    class: 'opioid',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: '1-2 minutes',
    duration: '30-60 minutes',
    dosing: {
      analgesia: '1-2 mcg/kg IV',
      induction: '2-10 mcg/kg IV',
      infusion: '0.5-2 mcg/kg/hr',
      intranasal: '1.5-2 mcg/kg (max 100 mcg)',
      units: 'mcg/kg'
    },
    hemodynamics: {
      HR: '↓ (bradycardia)',
      BP: '↓',
      SVR: '↔',
      contractility: '↔'
    },
    uses: [
      'Intraoperative analgesia',
      'Blunt intubation response',
      'Postoperative pain',
      'Procedural sedation'
    ],
    considerations: [
      'Dose-dependent respiratory depression',
      'Chest wall rigidity with rapid high-dose bolus',
      'May cause bradycardia (more common in children)',
      'Minimal hemodynamic effects at moderate doses',
      'Shorter duration in neonates (immature metabolism)',
      'Crosses blood-brain barrier rapidly',
      'Intranasal route useful for needle-phobic children',
      'No histamine release',
      'Renally cleared metabolites may accumulate'
    ],
    bestFor: 'Intraoperative analgesia, blunting intubation response'
  },
  {
    name: 'Morphine',
    proprietaryName: 'Various',
    class: 'opioid',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: '5-10 minutes (IV)',
    duration: '3-5 hours',
    dosing: {
      analgesia: '0.05-0.1 mg/kg IV/IM q2-4h',
      neonates: '0.03-0.05 mg/kg (lower doses)',
      infusion: '10-30 mcg/kg/hr',
      po: '0.2-0.5 mg/kg PO q4-6h',
      units: 'mg/kg'
    },
    hemodynamics: {
      HR: '↓',
      BP: '↓',
      SVR: '↓ (histamine release)',
      contractility: '↔'
    },
    uses: [
      'Moderate to severe pain',
      'Postoperative analgesia',
      'ICU sedation/analgesia'
    ],
    considerations: [
      'Histamine release - may cause hypotension, pruritis',
      'Active metabolite (M6G) - renally cleared',
      'Increased risk apnea in neonates (immature metabolism)',
      'Reduce dose in renal failure',
      'Longer duration than fentanyl',
      'May cause nausea, vomiting, constipation',
      'Codeine safer alternative in former preemies (avoid morphine)',
      'Avoid in renal failure (metabolite accumulation)'
    ],
    bestFor: 'Postoperative pain, especially when long duration needed'
  },
  {
    name: 'Hydromorphone',
    proprietaryName: 'Dilaudid',
    class: 'opioid',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: '5-10 minutes',
    duration: '3-4 hours',
    dosing: {
      analgesia: '0.01-0.02 mg/kg IV q3-4h',
      po: '0.04-0.08 mg/kg PO q4-6h',
      infusion: '3-5 mcg/kg/hr',
      units: 'mg/kg'
    },
    hemodynamics: {
      HR: '↓',
      BP: '↓',
      SVR: '↔',
      contractility: '↔'
    },
    uses: [
      'Moderate to severe pain',
      'Alternative to morphine',
      'Patient-controlled analgesia (PCA)'
    ],
    considerations: [
      '5-7x more potent than morphine',
      'Less histamine release than morphine',
      'Better hemodynamic stability than morphine',
      'Similar duration to morphine',
      'Active metabolites (less significant than morphine)',
      'Good alternative in renal failure',
      'May cause less nausea than morphine',
      'Preferred over morphine in many institutions'
    ],
    bestFor: 'Postoperative analgesia when morphine not tolerated'
  },

  // MUSCLE RELAXANTS
  {
    name: 'Rocuronium',
    proprietaryName: 'Zemuron',
    class: 'muscle-relaxant',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: '60-90 seconds (dose-dependent)',
    duration: '30-45 minutes',
    dosing: {
      intubation: '0.6-1.2 mg/kg IV (1.2 mg/kg for RSI)',
      maintenance: '0.15 mg/kg IV q20-30 min',
      infusion: '5-12 mcg/kg/min',
      rsi: '1.2-1.5 mg/kg IV (45-60 sec onset)',
      units: 'mg/kg'
    },
    uses: [
      'Intubation (including RSI)',
      'Muscle relaxation for surgery',
      'Facilitation of mechanical ventilation'
    ],
    considerations: [
      'Most commonly used NMBD in children',
      'Rapid onset at 1.2 mg/kg (RSI-appropriate)',
      'Fully reversible with sugammadex',
      'No histamine release',
      'Minimal cardiovascular effects',
      'Primarily hepatic elimination',
      'Longer duration in neonates',
      'Higher doses needed in infants (larger Vd)',
      'Vagolytic at high doses (may increase HR)'
    ],
    bestFor: 'First-line NMBD for intubation and maintenance'
  },
  {
    name: 'Succinylcholine',
    proprietaryName: 'Anectine',
    class: 'muscle-relaxant',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: '30-60 seconds',
    duration: '5-10 minutes',
    dosing: {
      iv: '2 mg/kg IV (higher than adults)',
      im: '4-5 mg/kg IM (if no IV access)',
      infants: '2-3 mg/kg IV',
      units: 'mg/kg'
    },
    uses: [
      'Rapid sequence intubation',
      'Emergency airway management',
      'Laryngospasm treatment'
    ],
    considerations: [
      'HIGHER DOSE in children vs adults (2 mg/kg vs 1-1.5 mg/kg)',
      'Only depolarizing NMBD',
      'Risk of malignant hyperthermia',
      'Contraindicated in muscular dystrophy, burn >24h, crush injury',
      'May cause bradycardia (give atropine first in children <5 yr)',
      'Fasciculations less common in children',
      'Increases ICP, IOP, intragastric pressure',
      'May cause hyperkalemia (especially with repeat dosing)',
      'Use with caution in family history of MH',
      'IM route useful for laryngospasm without IV access'
    ],
    bestFor: 'Cannot intubate/ventilate, RSI, laryngospasm'
  },
  {
    name: 'Cisatracurium',
    proprietaryName: 'Nimbex',
    class: 'muscle-relaxant',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: '2-3 minutes',
    duration: '30-50 minutes',
    dosing: {
      intubation: '0.15-0.2 mg/kg IV',
      maintenance: '0.03 mg/kg IV',
      infusion: '1-3 mcg/kg/min',
      units: 'mg/kg'
    },
    uses: [
      'Intubation (non-RSI)',
      'Maintenance of neuromuscular blockade',
      'Patients with renal/hepatic dysfunction'
    ],
    considerations: [
      'Hoffman elimination (organ-independent)',
      'Excellent for renal/hepatic failure',
      'No histamine release',
      'Minimal cardiovascular effects',
      'Longer onset than rocuronium',
      'Intermediate duration',
      'Predictable offset regardless of organ function',
      'More expensive than rocuronium',
      'Good for ICU patients with multiorgan dysfunction'
    ],
    bestFor: 'Patients with renal/hepatic dysfunction'
  },

  // REVERSAL AGENTS
  {
    name: 'Sugammadex',
    proprietaryName: 'Bridion',
    class: 'reversal',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: '1-3 minutes',
    duration: 'Complete reversal within 5 minutes',
    dosing: {
      moderate: '2 mg/kg IV (if TOF 2-4 twitches)',
      deep: '4 mg/kg IV (if PTC 1-2)',
      immediate: '16 mg/kg IV (if given 3 min post-rocuronium)',
      units: 'mg/kg'
    },
    uses: [
      'Reversal of rocuronium or vecuronium',
      'Rapid reversal of neuromuscular blockade',
      'Cannot intubate/ventilate scenario'
    ],
    considerations: [
      'Encapsulates rocuronium/vecuronium (not enzymatic)',
      'Rapid, reliable reversal regardless of depth',
      'Expensive but increasingly used in pediatrics',
      'Dose-dependent: 2 mg/kg (moderate), 4 mg/kg (deep), 16 mg/kg (immediate)',
      'May affect hormonal contraceptives',
      'Can cause bradycardia (especially with second dose)',
      'No ceiling effect - complete reversal always possible',
      'Superior to neostigmine for deep block',
      'Safe in children of all ages'
    ],
    bestFor: 'Rapid, reliable reversal of rocuronium/vecuronium'
  },
  {
    name: 'Neostigmine',
    proprietaryName: 'Bloxiverz',
    class: 'reversal',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: '5-15 minutes',
    duration: '30-60 minutes',
    dosing: {
      reversal: '0.03-0.07 mg/kg IV (max 5 mg)',
      withGlycopyrrolate: 'Give glycopyrrolate 0.01 mg/kg (or atropine 0.02 mg/kg)',
      units: 'mg/kg'
    },
    uses: [
      'Reversal of non-depolarizing NMBDs',
      'Alternative to sugammadex'
    ],
    considerations: [
      'Acetylcholinesterase inhibitor',
      'MUST give with anticholinergic (glycopyrrolate or atropine)',
      'Slower onset than sugammadex',
      'Cannot reverse deep blockade reliably',
      'Should have at least 2 twitches on TOF',
      'May cause bradycardia if given without anticholinergic',
      'Increases secretions, bronchospasm (muscarinic effects)',
      'Much less expensive than sugammadex',
      'Adequate for routine reversal with shallow block'
    ],
    bestFor: 'Cost-effective reversal when block is shallow'
  },

  // EMERGENCY/RESUSCITATION DRUGS
  {
    name: 'Epinephrine (Pediatric)',
    proprietaryName: 'Adrenaline',
    class: 'emergency',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: 'IV: Immediate',
    duration: '3-5 minutes',
    dosing: {
      cardiac_arrest: '0.01 mg/kg IV/IO (0.1 mL/kg of 1:10,000) q3-5min',
      et: '0.1 mg/kg ET (0.1 mL/kg of 1:1,000)',
      anaphylaxis: '0.01 mg/kg IM (0.01 mL/kg of 1:1,000, max 0.5 mg)',
      bradycardia: '0.01 mg/kg IV/IO',
      infusion: '0.05-1 mcg/kg/min',
      units: 'mg/kg or mcg/kg/min'
    },
    hemodynamics: {
      HR: '↑↑',
      BP: '↑↑↑',
      SVR: '↑↑↑',
      contractility: '↑↑↑'
    },
    uses: [
      'Cardiac arrest (PALS)',
      'Anaphylaxis',
      'Severe bradycardia with poor perfusion',
      'Severe hypotension/shock'
    ],
    considerations: [
      'PALS dose: 0.01 mg/kg (1:10,000) IV/IO q3-5min',
      'First-line for cardiac arrest in children',
      'IM route for anaphylaxis (NOT IV bolus)',
      'May give via ETT if no IV/IO access (0.1 mg/kg)',
      'Infusion for persistent shock',
      'High alert medication - double-check dose',
      'Different concentrations for different routes',
      '1:10,000 (0.1 mg/mL) for IV, 1:1,000 (1 mg/mL) for IM/ET',
      'Ensure correct dilution to avoid errors'
    ],
    bestFor: 'First-line medication for pediatric cardiac arrest'
  },
  {
    name: 'Atropine',
    proprietaryName: 'AtroPen',
    class: 'emergency',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: '1-2 minutes',
    duration: '30-60 minutes',
    dosing: {
      bradycardia: '0.02 mg/kg IV (minimum 0.1 mg, max single dose 0.5 mg)',
      premedication: '0.01-0.02 mg/kg IV/IM',
      organophosphate: '0.05 mg/kg IV/IM q10-20min',
      units: 'mg/kg'
    },
    hemodynamics: {
      HR: '↑↑',
      BP: '↑',
      SVR: '↔',
      contractility: '↔'
    },
    uses: [
      'Bradycardia with poor perfusion',
      'Antisialogogue (ketamine, succinylcholine)',
      'Organophosphate/nerve agent poisoning'
    ],
    considerations: [
      'MINIMUM dose: 0.1 mg (paradoxical bradycardia with lower doses)',
      'MAX single dose: 0.5 mg in children',
      'May cause tachycardia, flushing, mydriasis',
      'Less effective than epinephrine for cardiac arrest',
      'Good for vagally-mediated bradycardia',
      'Crosses blood-brain barrier (CNS effects possible)',
      'Preferred over glycopyrrolate for emergency bradycardia',
      'Always give before succinylcholine in children <5 yr'
    ],
    bestFor: 'Vagally-mediated bradycardia, antisialogogue'
  },
  {
    name: 'Dexmedetomidine',
    proprietaryName: 'Precedex',
    class: 'sedative',
    categories: ['pediatric'],
    primaryCategory: 'pediatric',
    onset: '5-10 minutes',
    duration: 'Context-sensitive (1-2 hrs after infusion)',
    dosing: {
      loading: '0.5-1 mcg/kg IV over 10 min',
      infusion: '0.2-0.7 mcg/kg/hr',
      intranasal: '1-2 mcg/kg (premedication)',
      units: 'mcg/kg'
    },
    hemodynamics: {
      HR: '↓',
      BP: '↓ or ↑ (biphasic)',
      SVR: '↓',
      contractility: '↓'
    },
    uses: [
      'Sedation without respiratory depression',
      'Premedication (intranasal)',
      'Emergence delirium prevention/treatment',
      'ICU sedation'
    ],
    considerations: [
      'α2-agonist - sedation without respiratory depression',
      'Excellent for anxious/uncooperative children',
      'Intranasal route effective for premedication (2 mcg/kg)',
      'May cause bradycardia and hypotension',
      'Biphasic BP response: initial ↑ then ↓',
      'Reduces opioid and volatile requirements',
      'Prevents/treats emergence delirium',
      'Minimal effect on ICP',
      'Increasingly used in pediatric anesthesia'
    ],
    bestFor: 'Premedication, emergence delirium, cooperative sedation'
  }
];

// Organized exports by class
export const inductionAgents = pediatricMedications.filter(med => med.class === 'induction' || med.class === 'volatile');
export const pediatricOpioids = pediatricMedications.filter(med => med.class === 'opioid');
export const muscleRelaxants = pediatricMedications.filter(med => med.class === 'muscle-relaxant');
export const reversalAgents = pediatricMedications.filter(med => med.class === 'reversal');
export const emergencyDrugs = pediatricMedications.filter(med => med.class === 'emergency' || med.class === 'sedative');

export default pediatricMedications;
